/**
 * Pure selector logic for the Saves feature.
 *
 * These helpers are the single source of truth for how saves are scoped to a
 * view (Unread / Read / All / a custom tab / Archived), how filters are
 * applied, and how the per-tab counters are computed. Both the Saves list
 * contents and the tab counters must derive from this same pipeline so they
 * can never disagree.
 *
 * Tab membership is *additive*: a save that qualifies for a custom tab (via
 * its `tabId` or via the tab's `autoAddTags` rules) still belongs to the base
 * Unread / Read / All sets. Qualifying a save into a tab never excludes it
 * from those sets.
 */
import { SAVE_SORT_OPTIONS, SAVE_READ_STATUS_FILTERS } from '../constants/saveConstants';

/** Built-in view modes alongside custom tab ids. */
export const BASE_VIEW_MODES = {
    UNREAD: 'unread',
    READ: 'read',
    ACTIVE: 'active',
    ARCHIVED: 'archived',
};

/**
 * Normalize a string for case-insensitive comparisons.
 * @param {string} value
 * @returns {string}
 */
const normalize = (value) => (typeof value === 'string' ? value.toLowerCase() : '');

/**
 * Coerce the various timestamp shapes used by saves (Firestore Timestamp,
 * `{ seconds }` objects, Date instances, epoch millis) into epoch millis.
 * @param {*} date
 * @returns {number|null} epoch millis, or null when unparseable
 */
export const getSaveTimestamp = (date) => {
    if (!date) return null;
    if (typeof date.toDate === 'function') return date.toDate().getTime();
    if (typeof date === 'object' && date.seconds) return new Date(date.seconds * 1000).getTime();
    const parsed = new Date(date).getTime();
    return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Test a save against the free-text search term (title or URL).
 * @param {object} save
 * @param {string} [searchTerm]
 * @returns {boolean}
 */
export const matchesSearchTerm = (save, searchTerm = '') => {
    const term = normalize(searchTerm);
    if (!term) return true;
    return normalize(save.title).includes(term) || normalize(save.url).includes(term);
};

/**
 * Test a save against the selected tag filter (case-insensitive names).
 * @param {object} save
 * @param {Array<{name: string}>} [selectedTags]
 * @returns {boolean}
 */
export const matchesSelectedTags = (save, selectedTags = []) => {
    if (!selectedTags || selectedTags.length === 0) return true;
    if (!save.tags || save.tags.length === 0) return false;
    return save.tags.some(tag =>
        selectedTags.some(selectedTag => normalize(selectedTag.name) === normalize(tag.name))
    );
};

/**
 * Test a save against the read-status filter. A missing `read` flag counts as
 * unread, which is how saves are initialized.
 *
 * @param {object} save
 * @param {string} [readStatus] 'all' | 'read' | 'unread'
 * @returns {boolean}
 */
export const matchesReadStatus = (save, readStatus = SAVE_READ_STATUS_FILTERS.ALL) => {
    if (readStatus === SAVE_READ_STATUS_FILTERS.READ) return Boolean(save && save.read);
    if (readStatus === SAVE_READ_STATUS_FILTERS.UNREAD) return !(save && save.read);
    return true;
};

/**
 * Test a save against an optional date range applied to `createdAt`.
 * @param {object} save
 * @param {{from?: Date|number|string, to?: Date|number|string}|null} [dateRange]
 * @returns {boolean}
 */
export const matchesDateRange = (save, dateRange) => {
    if (!dateRange || (!dateRange.from && !dateRange.to)) return true;
    const createdAt = getSaveTimestamp(save.createdAt);
    if (createdAt === null) return false;
    if (dateRange.from && createdAt < getSaveTimestamp(dateRange.from)) return false;
    if (dateRange.to && createdAt > getSaveTimestamp(dateRange.to)) return false;
    return true;
};

/**
 * Test whether a save qualifies for a custom tab, either by explicit `tabId`
 * assignment or by the tab's `autoAddTags` rules (case-insensitive).
 * @param {object} save
 * @param {{id: string, autoAddTags?: string[]}} tab
 * @returns {boolean}
 */
export const matchesTabQualification = (save, tab) => {
    if (!tab) return false;
    if (save.tabId && save.tabId === tab.id) return true;
    const autoAddTags = (tab.autoAddTags || []).map(normalize);
    if (autoAddTags.length === 0) return false;
    return Boolean(save.tags && save.tags.some(tag => autoAddTags.includes(normalize(tag.name))));
};

/**
 * Resolve the pool of saves a given view is drawn from, before filters such
 * as search / tags / date range are applied. Tab membership is additive:
 * the Unread / Read / All views include tabbed saves as well.
 *
 * @param {object} params
 * @param {object[]} [params.saves] active (non-archived) saves
 * @param {object[]} [params.archivedSaves] archived saves
 * @param {string} [params.viewMode] 'unread' | 'read' | 'active' | 'archived' | custom tab id
 * @param {Array<{id: string, autoAddTags?: string[]}>} [params.tabs] custom tabs
 * @returns {object[]} saves belonging to the view (unfiltered, unsorted)
 */
export const getSavesForView = ({ saves = [], archivedSaves = [], viewMode = BASE_VIEW_MODES.ACTIVE, tabs = [] }) => {
    const customTab = tabs.find(tab => tab.id === viewMode);
    if (customTab) {
        return saves.filter(save => matchesTabQualification(save, customTab));
    }

    switch (viewMode) {
        case BASE_VIEW_MODES.READ:
            return saves.filter(save => save.read);
        case BASE_VIEW_MODES.UNREAD:
            return saves.filter(save => !save.read);
        case BASE_VIEW_MODES.ARCHIVED:
            return archivedSaves;
        case BASE_VIEW_MODES.ACTIVE:
        default:
            // 'active' (All): every non-archived save, regardless of tab membership.
            return saves;
    }
};

/**
 * Apply the shared filters (search, selected tags, date range, read status) to
 * a pool of saves. Every view and every counter is filtered through this
 * function.
 *
 * @param {object[]} savesList
 * @param {object} [filters]
 * @param {string} [filters.searchTerm]
 * @param {Array<{name: string}>} [filters.selectedTags]
 * @param {{from?: *, to?: *}|null} [filters.dateRange]
 * @param {string} [filters.readStatus] 'all' | 'read' | 'unread'
 * @returns {object[]} saves matching all active filters
 */
export const applySaveFilters = (savesList, filters = {}) => {
    const { searchTerm = '', selectedTags = [], dateRange = null, readStatus = SAVE_READ_STATUS_FILTERS.ALL } = filters;
    return savesList.filter(save =>
        matchesSearchTerm(save, searchTerm) &&
        matchesSelectedTags(save, selectedTags) &&
        matchesDateRange(save, dateRange) &&
        matchesReadStatus(save, readStatus)
    );
};

/**
 * Sort saves according to one of `SAVE_SORT_OPTIONS`.
 * @param {object[]} savesList
 * @param {string} [sortBy]
 * @returns {object[]} a new sorted array
 */
export const sortSaves = (savesList, sortBy = SAVE_SORT_OPTIONS.RECENT) => {
    const getDateTime = (date) => getSaveTimestamp(date) || 0;

    return [...savesList].sort((a, b) => {
        const aHasCreatedAt = !!a.createdAt;
        const bHasCreatedAt = !!b.createdAt;

        if (sortBy === SAVE_SORT_OPTIONS.RECENT) {
            if (aHasCreatedAt && bHasCreatedAt) {
                return getDateTime(b.createdAt) - getDateTime(a.createdAt);
            } else if (aHasCreatedAt) { return -1; }
            else if (bHasCreatedAt) { return 1; }
            else { return a.title.localeCompare(b.title); }
        } else if (sortBy === SAVE_SORT_OPTIONS.OLDEST) {
            if (!aHasCreatedAt && !bHasCreatedAt) {
                return a.title.localeCompare(b.title);
            } else if (!aHasCreatedAt) { return -1; }
            else if (!bHasCreatedAt) { return 1; }
            else { return getDateTime(a.createdAt) - getDateTime(b.createdAt); }
        } else if (sortBy === SAVE_SORT_OPTIONS.ALPHA_ASC) {
            return a.title.localeCompare(b.title);
        } else if (sortBy === SAVE_SORT_OPTIONS.ALPHA_DESC) {
            return b.title.localeCompare(a.title);
        }
        return 0;
    });
};

/**
 * The full pipeline for the Saves list: scope to the view, apply the shared
 * filters, then sort. The tab counters are computed through the exact same
 * scoping and filtering functions (see `computeTabCounts`).
 *
 * @param {object} params
 * @param {object[]} [params.saves] active saves
 * @param {object[]} [params.archivedSaves] archived saves
 * @param {string} [params.viewMode]
 * @param {Array<{id: string, autoAddTags?: string[]}>} [params.tabs]
 * @param {object} [params.filters] `{ searchTerm, selectedTags, dateRange, readStatus }`
 * @param {string} [params.sortBy]
 * @returns {object[]} the saves shown in the list for the active view/filters
 */
export const filterAndSortSaves = ({ saves, archivedSaves, viewMode, tabs, filters, sortBy }) => {
    const pool = getSavesForView({ saves, archivedSaves, viewMode, tabs });
    return sortSaves(applySaveFilters(pool, filters), sortBy);
};

/**
 * List every view mode that has a counter: the built-in views, each custom
 * tab, and the archive.
 *
 * @param {Array<{id: string}>} [tabs]
 * @returns {string[]}
 */
export const getAllViewModes = (tabs = []) => [
    BASE_VIEW_MODES.UNREAD,
    BASE_VIEW_MODES.READ,
    BASE_VIEW_MODES.ACTIVE,
    ...tabs.map(tab => tab.id),
    BASE_VIEW_MODES.ARCHIVED,
];

/**
 * Compute the counter for every view/tab from the same scoping and filtering
 * pipeline used for the list. Because this is a pure function of the current
 * saves + filters, the counters always reflect saves added, removed,
 * re-qualified, read/unread toggled, or matched by active filters — no tab
 * switch or reload required.
 *
 * @param {object} params
 * @param {object[]} [params.saves] active saves
 * @param {object[]} [params.archivedSaves] archived saves
 * @param {Array<{id: string, autoAddTags?: string[]}>} [params.tabs] custom tabs
 * @param {object} [params.filters] `{ searchTerm, selectedTags, dateRange, readStatus }`
 * @returns {Object<string, number>} map of view mode / tab id -> save count
 */
export const computeTabCounts = ({ saves = [], archivedSaves = [], tabs = [], filters = {} } = {}) => {
    const counts = {};
    getAllViewModes(tabs).forEach(viewMode => {
        const pool = getSavesForView({ saves, archivedSaves, viewMode, tabs });
        counts[viewMode] = applySaveFilters(pool, filters).length;
    });
    return counts;
};
